import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaPlatform,
} from '@wix/panorama-client-web';
import type { PanoramaClient } from '@wix/panorama-client-web';

const FULL_ARTIFACT_ID = 'com.wixpress.users.login-react-app';
const COMPONENT_ID = 'login-react-app';

type PanoramaTransaction = ReturnType<PanoramaClient['transaction']>;
/** Optional per-transaction custom data, forwarded to Panorama start/finish. */
type TransactionData = Parameters<PanoramaTransaction['finish']>[0];

/**
 * Yoshi bakes the artifact version into the bundle at build time. Panorama
 * needs it for gradual-rollout attribution: without it the
 * "…Success Rate Delta" rollout rule can't tie transactions to a version.
 * Reading it here (rather than parsing the statics URL) works the same for
 * every template that loads the bundle. Local builds report "0.0.0".
 */
function createPanoramaClient(): PanoramaClient | undefined {
  // Client creation wires up plugins (sentry, environment, …); guard it so a
  // monitoring-setup failure can never break app init in the RootStore ctor.
  try {
    return panoramaClientFactory({
      baseParams: {
        fullArtifactId: FULL_ARTIFACT_ID,
        artifactVersion: process.env.ARTIFACT_VERSION,
        platform: PanoramaPlatform.Standalone,
      },
    })
      .withGlobalConfig(createGlobalConfig())
      .client({ baseParams: { componentId: COMPONENT_ID } });
  } catch {
    return undefined;
  }
}

/**
 * Panorama replaces the deprecated FedOps logger for this app. Component
 * transactions map 1:1 to the old FedOps interactions (`startTransaction` ↔
 * `interactionStarted`, `finishTransaction` ↔ `interactionEnded`) and the
 * component-load transaction replaces `appLoaded`. They also power the
 * Deployments-NG "Safer gradual rollout" gate ("Panorama Component Transaction
 * Success Rate Delta"). A transaction is a success once `finish()` is called;
 * a started-but-unfinished transaction is counted as a failure.
 */
export class PanoramaService {
  private readonly client: PanoramaClient | undefined;
  private readonly openTransactions = new Map<string, PanoramaTransaction>();

  constructor() {
    this.client = createPanoramaClient();
  }

  /** Starts the component-load transaction (page-load success rate). */
  reportLoadStart(): void {
    this.client?.reportLoadStart();
  }

  /** Marks the component load as successful. */
  reportLoadFinish(): void {
    this.client?.reportLoadFinish();
  }

  startTransaction(name: string, data?: TransactionData): void {
    const transaction = this.client?.transaction(name);
    if (transaction) {
      this.openTransactions.set(name, transaction);
      transaction.start(data);
    }
  }

  finishTransaction(name: string, data?: TransactionData): void {
    this.openTransactions.get(name)?.finish(data);
    this.openTransactions.delete(name);
  }
}
